import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, XCircle, KeyRound } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import CopyRight from '@/components/ui/copy-right';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { toast } = useToast();
  const token = searchParams.get('token');

  // Password validation rules
  const passwordValidation = useMemo(() => {
    const rules = [
      {
        id: 'length',
        label: 'At least 8 characters',
        isValid: password.length >= 8,
        icon: password.length >= 8 ? CheckCircle : XCircle,
        color: password.length >= 8 ? 'text-emerald-600' : 'text-red-600'
      },
      {
        id: 'uppercase',
        label: 'At least one uppercase letter',
        isValid: /[A-Z]/.test(password),
        icon: /[A-Z]/.test(password) ? CheckCircle : XCircle,
        color: /[A-Z]/.test(password) ? 'text-emerald-600' : 'text-red-600'
      },
      {
        id: 'lowercase',
        label: 'At least one lowercase letter',
        isValid: /[a-z]/.test(password),
        icon: /[a-z]/.test(password) ? CheckCircle : XCircle,
        color: /[a-z]/.test(password) ? 'text-emerald-600' : 'text-red-600'
      },
      {
        id: 'number',
        label: 'At least one number',
        isValid: /[0-9]/.test(password),
        icon: /[0-9]/.test(password) ? CheckCircle : XCircle,
        color: /[0-9]/.test(password) ? 'text-emerald-600' : 'text-red-600'
      },
      {
        id: 'special',
        label: 'At least one special character',
        isValid: /[^A-Za-z0-9]/.test(password),
        icon: /[^A-Za-z0-9]/.test(password) ? CheckCircle : XCircle,
        color: /[^A-Za-z0-9]/.test(password) ? 'text-emerald-600' : 'text-red-600'
      }
    ];

    return {
      rules,
      isValid: rules.every(rule => rule.isValid),
      validCount: rules.filter(rule => rule.isValid).length,
      totalCount: rules.length
    };
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your passwords match',
        variant: 'destructive',
      });
      return;
    }

    if (!passwordValidation.isValid) {
      toast({
        title: 'Password requirements not met',
        description: 'Please ensure your password meets all requirements',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(token, password);
      toast({
        title: 'Password reset successfully!',
        description: 'You can now sign in with your new password.',
        variant: 'default',
      });
      navigate('/login');
    } catch (error) {
      console.error('Password reset failed:', error);
      toast({
        title: 'Failed to reset password. Please try again.',
        description: 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // If no token is provided, show error
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative overflow-auto h-screen">
        <div className="absolute inset-0 bg-grid-pattern opacity-40"></div>
        <motion.div 
          className="w-full max-w-md relative z-10"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="shadow-lg border border-slate-200 rounded-lg bg-white">
            <CardHeader className="space-y-1 text-center">
              <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-xl font-semibold text-red-600 font-montserrat">
                Invalid Reset Link
              </CardTitle>
              <CardDescription className="text-slate-600">This password reset link is invalid or has expired.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/login')} 
                className="w-full h-10 rounded-lg bg-brand-teal hover:bg-brand-teal/90"
              >
                Return to Login
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative overflow-auto h-screen">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-40"></div>
      
      {/* Decorative Circles */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-teal/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-copper/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      <div className="w-full max-w-md relative z-10">
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="w-20 h-20 bg-brand-teal rounded-xl flex items-center justify-center mx-auto mb-6"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <KeyRound className="h-10 w-10 text-white" />
          </motion.div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-montserrat">
            Reset Your Password
          </h1>
          <p className="text-slate-600 mt-2 text-sm">Enter your new password below</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="shadow-lg border border-slate-200 rounded-lg bg-white">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-xl font-semibold text-slate-900 font-montserrat">Create New Password</CardTitle>
              <CardDescription className="text-slate-600">
                Your new password must meet all the requirements listed below.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 font-medium" style={{ fontSize: '0.75rem' }}>
                    New Password <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your new password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="h-10 pr-12 rounded-lg border-slate-200 focus:border-black focus:ring-0 transition-colors duration-200"
                      minLength={8}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-4 py-2 hover:bg-transparent text-slate-500 hover:text-slate-700"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                {/* Password validation feedback */}
                {password && (
                  <motion.div 
                    className="space-y-3 p-4 bg-slate-50 rounded-lg"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 font-medium">Password requirements:</span>
                      <span className={`font-semibold ${passwordValidation.isValid ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {passwordValidation.validCount}/{passwordValidation.totalCount} met
                      </span>
                    </div>
                    <div className="space-y-2">
                      {passwordValidation.rules.map((rule, index) => {
                        const IconComponent = rule.icon;
                        return (
                          <motion.div 
                            key={rule.id} 
                            className="flex items-center space-x-2 text-sm"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                          >
                            <IconComponent className={`h-4 w-4 ${rule.color}`} />
                            <span className={rule.color}>{rule.label}</span>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700 font-medium" style={{ fontSize: '0.75rem' }}>
                    Confirm New Password <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required
                      className="h-10 pr-12 rounded-lg border-slate-200 focus:border-black focus:ring-0 transition-colors duration-200"
                      minLength={8}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-4 py-2 hover:bg-transparent text-slate-500 hover:text-slate-700"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {password && confirmPassword && (
                  <motion.div 
                    className="flex items-center space-x-2 text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    {password === confirmPassword ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-emerald-600" />
                        <span className="text-emerald-600 font-medium">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-red-600" />
                        <span className="text-red-600 font-medium">Passwords do not match</span>
                      </>
                    )}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  className="w-full h-10 rounded-lg bg-brand-teal hover:bg-brand-teal/90"
                  disabled={isLoading || password !== confirmPassword || !passwordValidation.isValid}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Resetting Password...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Lock className="h-4 w-4" />
                      <span>Reset Password</span>
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div 
          className="mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <CopyRight />
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
